import { useState, useEffect } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Button,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Avatar,
  useDisclosure,
  HStack,
  VStack,
  Text,
} from '@chakra-ui/react'
import { Ghost, Settings, LogOut } from 'lucide-react'
import { getStoredApiKey } from '@/lib/storage/local-storage'
import { validateApiKey } from '@/lib/api/keys'
import { SignOutConfirmModal } from './SignOutConfirmModal'
import { getAvatarColor } from '@/lib/utils/avatar'

export function UserMenu() {
  const [hasApiKey, setHasApiKey] = useState(false)
  const [apiKey, setApiKey] = useState<string | null | undefined>(null)
  const [accountName, setAccountName] = useState<string | null>(null)

  const loadAccountInfo = async (key: string) => {
    try {
      const stats = await validateApiKey(key)
      if (stats.valid && stats.name) {
        setAccountName(stats.name)
      }
    } catch {
      // Ignore errors, just show fallback
    }
  }

  useEffect(() => {
    const key = getStoredApiKey()
    setHasApiKey(!!key)
    setApiKey(key)
    if (key) {
      loadAccountInfo(key)
    }
  }, [])

  // Listen for storage changes (from other components)
  useEffect(() => {
    const handleStorageChange = () => {
      const key = getStoredApiKey()
      setHasApiKey(!!key)
      setApiKey(key)
      if (key) {
        loadAccountInfo(key)
      } else {
        setAccountName(null)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('apikey-changed', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('apikey-changed', handleStorageChange)
    }
  }, [])

  const avatarColor = getAvatarColor(apiKey)
  const { isOpen: isSignOutOpen, onOpen: onSignOutOpen, onClose: onSignOutClose } = useDisclosure()

  const handleSignOutComplete = () => {
    setHasApiKey(false)
    setAccountName(null)
  }

  const displayName = accountName || 'Anonymous'

  if (!hasApiKey) {
    return (
      <Button
        as={RouterLink}
        to="/editor?auth=signup"
        size="sm"
        colorScheme="brand"
        variant="outline"
      >
        Try Anonymously
      </Button>
    )
  }

  return (
    <Menu>
      <MenuButton
        as={Button}
        variant="ghost"
        size="sm"
        p={1}
        borderRadius="full"
      >
        <Avatar
          size="sm"
          bg={avatarColor}
          icon={<Icon as={Ghost} boxSize={4} color="white" />}
        />
      </MenuButton>
      <MenuList minW="200px">
        {/* User info header */}
        <HStack px={3} py={2} spacing={3}>
          <Avatar
            size="sm"
            bg={avatarColor}
            icon={<Icon as={Ghost} boxSize={4} color="white" />}
          />
          <VStack align="start" spacing={0}>
            <Text fontWeight="medium" fontSize="sm">{displayName}</Text>
            <Text fontSize="xs" color="stone.500">(anonymous)</Text>
          </VStack>
        </HStack>
        <MenuDivider />
        <MenuItem
          as={RouterLink}
          to="/settings"
          icon={<Icon as={Settings} boxSize={4} />}
        >
          Settings
        </MenuItem>
        <MenuDivider />
        <MenuItem
          icon={<Icon as={LogOut} boxSize={4} />}
          onClick={onSignOutOpen}
          color="red.500"
        >
          Sign out
        </MenuItem>
      </MenuList>

      <SignOutConfirmModal
        isOpen={isSignOutOpen}
        onClose={onSignOutClose}
        onSignOut={handleSignOutComplete}
      />
    </Menu>
  )
}
